import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <HeaderContainer $scrolled={scrolled}>
      <HeaderContent>
        <Logo to="/">
          <LogoImage src="/logo_low.png" alt="FreakIT Logo" />
          <LogoTextContainer>
            <LogoText>FreakIT</LogoText>
            <LogoSubtext>Freak · Innovator · Trendsetter</LogoSubtext>
          </LogoTextContainer>
        </Logo>

        <DesktopNav>
          <NavLink to="/">홈</NavLink>
          <NavLink to="/socra">SOCRATEST</NavLink>
          <NavLink to="/hura">HuRA</NavLink>
          <NavLink to="/projects">사이드 프로젝트</NavLink>
          <NavLink to="/achievements">성과</NavLink>
          <NavLink to="/about">회사 소개</NavLink>
          <ContactButton to="/contact">문의하기</ContactButton>
        </DesktopNav>

        <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <MenuIcon open={mobileMenuOpen}>
            <span></span>
            <span></span>
            <span></span>
          </MenuIcon>
        </MobileMenuButton>
      </HeaderContent>

      <MobileNav open={mobileMenuOpen}>
        <MobileNavLink to="/">홈</MobileNavLink>
        <MobileNavLink to="/socra">SOCRATEST</MobileNavLink>
        <MobileNavLink to="/hura">HuRA</MobileNavLink>
        <MobileNavLink to="/projects" onClick={() => setMobileMenuOpen(false)}>사이드 프로젝트</MobileNavLink>
        <MobileNavLink to="/achievements">성과</MobileNavLink>
        <MobileNavLink to="/about">회사 소개</MobileNavLink>
        <MobileContactButton to="/contact">문의하기</MobileContactButton>
      </MobileNav>
    </HeaderContainer>
  );
};

export default Header;

const HeaderContainer = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: ${({ theme }) => theme.transitions.normal};
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(2, 6, 23, 0.85)' : 'transparent'};
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(20px)' : 'none'};
  border-bottom: ${({ $scrolled, theme }) =>
    $scrolled ? `1px solid ${theme.colors.border}` : 'none'};
  box-shadow: ${({ $scrolled, theme }) =>
    $scrolled ? theme.shadows.medium : 'none'};
`;

const HeaderContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    opacity: 0.8;
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
`;

const LogoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LogoText = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
`;

const LogoSubtext = styled.span`
  font-size: 0.65rem;
  color: #94a3b8;
  margin-top: -0.25rem;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.fast};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.accent};
    transition: ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.accent};

    &::after {
      width: 100%;
    }
  }
`;

const ContactButton = styled(Link)`
  padding: 0.6rem 1.5rem;
  background: ${({ theme }) => theme.colors.bgGradient2};
  color: white;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: ${({ theme }) => theme.shadows.small};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MenuIcon = styled.div<{ open: boolean }>`
  width: 24px;
  height: 20px;
  position: relative;
  
  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transition: ${({ theme }) => theme.transitions.fast};
    
    &:nth-child(1) {
      top: ${({ open }) => open ? '9px' : '0px'};
      transform: ${({ open }) => open ? 'rotate(45deg)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      top: 9px;
      opacity: ${({ open }) => open ? '0' : '1'};
    }
    
    &:nth-child(3) {
      top: ${({ open }) => open ? '9px' : '18px'};
      transform: ${({ open }) => open ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileNav = styled.nav<{ open: boolean }>`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.bgPrimary === 'transparent' ? '#0f172a' : theme.colors.bgPrimary};
    padding: ${({ open }) => open ? '1rem 2rem 2rem' : '0 2rem'};
    max-height: ${({ open }) => open ? '400px' : '0'};
    overflow: hidden;
    transition: ${({ theme }) => theme.transitions.cubic};
    border-bottom: ${({ open, theme }) => 
      open ? `1px solid ${theme.colors.border}` : 'none'};
    box-shadow: ${({ open, theme }) => 
      open ? theme.shadows.medium : 'none'};
  }
`;

const MobileNavLink = styled(Link)`
  padding: 1rem 0;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    padding-left: 0.5rem;
  }
`;

const MobileContactButton = styled(Link)`
  margin-top: 1rem;
  padding: 0.8rem 1.5rem;
  background: ${({ theme }) => theme.colors.bgGradient2};
  color: white;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.9;
  }
`;
